// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/services/financialService.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.6.0 – FIX: Filtro Reforçado de Duplicidade (String Check)
 * Impacto da Versão: [src/services/financialService.js]
 * Data: 13/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [CORREÇÃO]: Aprimoramento do filtro em `getLedgerEntries`.
 * - MOTIVO: O filtro anterior apenas por flag técnica não pegou registros legados.
 * - SOLUÇÃO: Agora ignoramos explicitamente transações financeiras cuja descrição comece com "VENDA PDV", 
 * pois a venda original já é contabilizada pela leitura da coleção 'sales'.
 */
//

import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  Timestamp, 
  orderBy 
} from 'firebase/firestore';
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from '../utils/constants';

// [GS-CONFIG]: Coleções do Firestore
const LEDGER_COLLECTION = 'financial_transactions'; // Coleção Legada/Produção
const SALES_COLLECTION = 'sales';                   // Coleção de Vendas
const USERS_COLLECTION = 'users';

const financialService = {

  /**
   * [GS-NOVO] Função Especializada para Baixa de Contas a Pagar.
   * Garante que o registro no Ledger nasça PAGO e como DESPESA.
   */
  registerExpenseFromPayment: async (params) => {
    try {
      const issueDate = params.date ? new Date(params.date) : new Date();
      
      const payload = {
        amountGross: -Math.abs(params.amount), 
        amountNet: -Math.abs(params.amount),
        
        // [GS-LEGACY-WRITE]
        amount: -Math.abs(params.amount), 
        date: Timestamp.fromDate(issueDate),
        categoryName: params.category || 'Despesas Operacionais',
        
        description: params.description,
        category: params.category || 'Despesas Operacionais',
        type: FINANCIAL_TYPES.EXPENSE, 
        status: FINANCIAL_STATUS.PAID, 
        paymentMethod: params.method,
        dates: {
          issue: Timestamp.fromDate(issueDate),
          payment: Timestamp.fromDate(issueDate),
        },
        createdAt: Timestamp.now(),
        source: 'accounts_payable_module', 
        referenceId: params.referenceId
      };

      const docRef = await addDoc(collection(db, LEDGER_COLLECTION), payload);
      console.log(`[Financeiro] Despesa de conta paga registrada: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error("[Financeiro] Erro crítico ao registrar pagamento:", error);
      throw error;
    }
  },

  /**
   * Adiciona uma nova entrada genérica no Livro Razão (Ledger).
   */
  addEntry: async (entryData) => {
    try {
      if (!entryData.amountGross && entryData.amountGross !== 0) {
        throw new Error("O valor bruto (amountGross) é obrigatório.");
      }
      if (!entryData.description) {
        throw new Error("A descrição da transação é obrigatória.");
      }

      const issueDate = entryData.dates?.issue ? new Date(entryData.dates.issue) : new Date();
      const entryType = entryData.type || (entryData.amountGross >= 0 ? FINANCIAL_TYPES.INCOME : FINANCIAL_TYPES.EXPENSE);

      const payload = {
        ...entryData,
        type: entryType,
        createdAt: Timestamp.now(),
        dates: {
          issue: Timestamp.fromDate(issueDate),
          due: entryData.dates?.due ? Timestamp.fromDate(new Date(entryData.dates.due)) : null,
          payment: entryData.dates?.payment ? Timestamp.fromDate(new Date(entryData.dates.payment)) : null,
        },
        status: entryData.status || FINANCIAL_STATUS.PENDING,
        amountNet: entryData.amountNet !== undefined ? entryData.amountNet : entryData.amountGross,
        
        // [GS-LEGACY-WRITE]
        date: Timestamp.fromDate(issueDate),
        amount: entryData.amountNet !== undefined ? entryData.amountNet : entryData.amountGross,
        categoryName: typeof entryData.category === 'string' ? entryData.category : (entryData.category?.name || 'Geral')
      };

      const docRef = await addDoc(collection(db, LEDGER_COLLECTION), payload);
      return docRef.id;

    } catch (error) {
      console.error("[Financeiro] Erro ao adicionar entrada:", error);
      throw error;
    }
  },

  /**
   * Busca as entradas do Ledger (Extrato) com filtros.
   * [GS-UNIFIED]: Busca em Transactions (Despesas/Manuais) E Sales (Vendas).
   */
  getLedgerEntries: async (filters = {}) => {
    try {
      // 1. Configurar Datas
      let startTimestamp = null;
      let endTimestamp = null;

      if (filters.startDate) {
        const [y, m, d] = filters.startDate.split('-').map(Number);
        const start = new Date(y, m - 1, d, 0, 0, 0, 0); 
        startTimestamp = Timestamp.fromDate(start);
      }

      if (filters.endDate) {
        const [y, m, d] = filters.endDate.split('-').map(Number);
        const end = new Date(y, m - 1, d, 23, 59, 59, 999);
        endTimestamp = Timestamp.fromDate(end);
      }

      // 2. Query Transactions
      const transactionsRef = collection(db, LEDGER_COLLECTION);
      let qTrans = query(transactionsRef, orderBy('date', 'desc')); 

      if (startTimestamp) qTrans = query(qTrans, where('date', '>=', startTimestamp));
      if (endTimestamp) qTrans = query(qTrans, where('date', '<=', endTimestamp));

      // 3. Query Sales
      const salesRef = collection(db, SALES_COLLECTION);
      let qSales = query(salesRef, orderBy('createdAt', 'desc'));

      if (startTimestamp) qSales = query(qSales, where('createdAt', '>=', startTimestamp));
      if (endTimestamp) qSales = query(qSales, where('createdAt', '<=', endTimestamp));

      // 4. Executar em Paralelo
      const [transSnap, salesSnap] = await Promise.all([
        getDocs(qTrans),
        getDocs(qSales)
      ]);

      const combinedEntries = [];

      // 5. Processar Transações (Legado)
      transSnap.forEach(doc => {
        const d = doc.data();

        // [GS-FIX-DUPLICITY-V2 - 13/01/2026]
        // Filtro de Blindagem: Ignora registros nesta coleção que sejam cópias de vendas.
        // A fonte oficial de vendas é a coleção 'sales' (query salesSnap abaixo).
        
        const descUpper = (d.description || '').toUpperCase();
        
        if (
            d.originModule === 'sales' || 
            d.source === 'sales' || 
            descUpper.startsWith('VENDA PDV') // Bloqueia duplicatas legadas do PDV
        ) {
            return;
        }

        const rawAmount = d.amountNet !== undefined ? d.amountNet : (d.amount || 0);
        const rawDate = d.dates?.issue ? d.dates.issue.toDate() : (d.date ? d.date.toDate() : new Date());
        
        let rawType = d.type;
        if (!rawType) rawType = Number(rawAmount) >= 0 ? FINANCIAL_TYPES.INCOME : FINANCIAL_TYPES.EXPENSE;
        if (rawType === 'income') rawType = FINANCIAL_TYPES.INCOME;
        if (rawType === 'expense') rawType = FINANCIAL_TYPES.EXPENSE;
        
        let rawStatus = d.status;
        if (!rawStatus) rawStatus = FINANCIAL_STATUS.PAID; 
        if (rawStatus === 'paid') rawStatus = FINANCIAL_STATUS.PAID;

        combinedEntries.push({
          id: doc.id,
          ...d,
          amountNet: Number(rawAmount),
          description: d.description || 'Sem descrição',
          category: d.category ? (typeof d.category === 'object' ? d.category.name : d.category) : (d.categoryName || 'Geral'),
          type: rawType,
          status: rawStatus,
          dateObj: rawDate,
          source: 'ledger'
        });
      });

      // 6. Processar Vendas (Sales) -> Receitas
      salesSnap.forEach(doc => {
        const s = doc.data();
        const st = (s.status || '').toLowerCase();
        
        if (st.includes('cancel') || st.includes('recusado') || st.includes('estornado') || st.includes('reembolsado')) {
            return;
        }

        const saleDate = s.createdAt ? s.createdAt.toDate() : (s.date ? s.date.toDate() : new Date());
        const saleValue = Number(s.total || s.totalValue || 0);

        if (saleValue > 0) {
            combinedEntries.push({
                id: doc.id,
                description: `Venda PDV #${doc.id.substring(0, 6).toUpperCase()}`,
                category: 'Vendas de Produtos',
                type: FINANCIAL_TYPES.INCOME, 
                status: FINANCIAL_STATUS.PAID, 
                amountNet: saleValue,
                amountGross: saleValue,
                dateObj: saleDate,
                dates: { issue: Timestamp.fromDate(saleDate) },
                source: 'sales',
                paymentBreakdown: { method: s.paymentMethod || 'Outros' }
            });
        }
      });

      // 7. Ordenar Unificado por Data (Decrescente)
      return combinedEntries.sort((a, b) => b.dateObj - a.dateObj);

    } catch (error) {
      console.error("[Financeiro] Erro ao buscar extrato unificado:", error);
      throw error;
    }
  },

  /**
   * Calcula o saldo (Entradas - Saídas).
   */
  getBalance: async (filters = {}) => {
    try {
      const entries = await financialService.getLedgerEntries(filters);
      
      let totalIncome = 0;
      let totalExpense = 0;

      entries.forEach((item) => {
        const val = Number(item.amountNet || 0);
        const status = (item.status || '').toLowerCase();
        
        const isPaid = status === 'paid' || 
                       status === FINANCIAL_STATUS.PAID || 
                       status === 'concluido' || 
                       filters.includePending;

        if (isPaid) {
           if (item.type === FINANCIAL_TYPES.INCOME || item.type === 'income') {
             totalIncome += Math.abs(val);
           } else {
             totalExpense += Math.abs(val);
           }
        }
      });

      return {
        income: totalIncome,
        expense: totalExpense,
        balance: totalIncome - totalExpense
      };

    } catch (error) {
      console.error("[Financeiro] Erro ao calcular saldo:", error);
      throw error;
    }
  },

  getLiability: async (userId) => {
    if (!userId) return 0;
    try {
      const batchesRef = collection(db, USERS_COLLECTION, userId, 'point_batches');
      const q = query(batchesRef, where('status', '==', 'ACTIVE'));
      const querySnapshot = await getDocs(q);
      let total = 0;
      querySnapshot.forEach((doc) => total += Number(doc.data().amountRemaining || 0));
      return total;
    } catch (error) {
      console.error(`[Financeiro] Erro liability user ${userId}:`, error);
      return 0;
    }
  }
};

export default financialService;